<template>
    <div class="max-w-4xl mx-auto py-8 px-4">
        <!-- Steadily Logo -->
        <div class="mb-8 flex justify-center">
            <div class="bg-white rounded-full p-4 shadow-sm border border-gray-100">
                <img :src="appUrl('/steadily-logo.png')" class="w-24 h-24" alt="Steadily Logo">
            </div>
        </div>

        <!-- Insurance Quote Information Card -->
        <div v-if="insuranceData?.steadily_quote" class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div class="text-center md:text-left">
                    <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Size</p>
                    <p class="text-2xl font-bold text-gray-900">
                        {{ insuranceData.steadily_quote.size_sqft || 'N/A' }} <span class="text-lg font-normal text-gray-600">sqft</span>
                    </p>
                </div>
                <div class="text-center md:text-left border-t md:border-t-0 md:border-l border-gray-200 pt-4 md:pt-0 md:pl-6">
                    <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Year Built</p>
                    <p class="text-2xl font-bold text-gray-900">
                        {{ insuranceData.steadily_quote.year_built || 'N/A' }}
                    </p>
                </div>
                <div class="text-center md:text-left border-t md:border-t-0 md:border-l border-gray-200 pt-4 md:pt-0 md:pl-6">
                    <p class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Annual Premium</p>
                    <p v-if="insuranceData.steadily_quote.dp_3" class="text-2xl font-bold text-purple-700">
                        ${{ insuranceData.steadily_quote.formatted_dp3 }}
                    </p>
                    <p
                        v-if="insuranceData.steadily_quote.dp_3"
                        class="text-xs text-gray-500 mt-1"
                        title="Approximate. Actual monthly cost may vary depending on the payment plan you choose with Steadily."
                    >
                        ≈ ${{ monthlyEstimate(insuranceData.steadily_quote.dp_3) }}/mo (estimated)
                    </p>
                    <p v-else class="text-lg font-semibold text-gray-400">
                        No quote yet
                    </p>
                </div>
            </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-center gap-3 flex-wrap items-center">
            <button
                type="button"
                :disabled="isSharedViewOnly"
                @click="getInsurance"
                class="primaryButton px-8 py-3 text-base font-semibold shadow-md hover:shadow-lg transition-shadow duration-200 disabled:pointer-events-none disabled:opacity-50"
            >
                Get Insurance Quote
            </button>
            <button
                type="button"
                :disabled="isSharedViewOnly"
                @click="openUploadModal"
                class="primaryButton px-8 py-3 text-base font-semibold shadow-md hover:shadow-lg transition-shadow duration-200 disabled:pointer-events-none disabled:opacity-50"
            >
                Upload My Policy
            </button>
            <ToolTip
                v-if="isSharedViewOnly && viewOnlyTooltip"
                :title="viewOnlyTooltip.title"
                :description="viewOnlyTooltip.description"
            />
        </div>

        <!-- Uploaded Document Display -->
        <div v-if="insuranceDocument" class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-8">
            <div class="flex items-center justify-between">
                <div class="flex items-center space-x-4">
                    <div class="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <svg class="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <div>
                        <a :href="insuranceDocument.url" target="_blank" class="text-sm font-medium text-purple-700 hover:text-purple-900 underline">
                            {{ insuranceDocument.file_name }}
                        </a>
                        <p class="text-xs text-gray-500 mt-0.5">
                            Uploaded {{ insuranceDocument.uploaded_at }}
                        </p>
                    </div>
                </div>
                <div class="flex items-center space-x-2">
                    <button
                        type="button"
                        :disabled="isSharedViewOnly"
                        @click="openUploadModal"
                        class="text-sm text-gray-600 hover:text-purple-700 font-medium px-3 py-1.5 border border-gray-300 rounded-md hover:border-purple-300 transition-colors disabled:pointer-events-none disabled:opacity-50"
                    >
                        Replace
                    </button>
                    <button
                        type="button"
                        :disabled="isSharedViewOnly"
                        @click="confirmDelete"
                        class="text-sm text-red-600 hover:text-red-800 font-medium px-3 py-1.5 border border-red-300 rounded-md hover:border-red-400 transition-colors disabled:pointer-events-none disabled:opacity-50"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>

        <!-- Get Insurance Quote Modal -->
        <ModalJetstream :show="getInsuranceModal" :max-width="'3xl'">
            <div class="bg-white rounded-lg p-6 m-2">
                <div class="mb-5 flex items-center justify-between border-b pb-3">
                    <h1 class="text-2xl font-bold text-gray-800">{{ property.full_address }}</h1>
                    <button @click="closeInsuranceModal"
                        class="text-gray-500 hover:text-gray-700 focus:outline-none">
                        <font-awesome-icon icon="fa-xmark" class="w-6 h-6" />
                    </button>
                </div>
                <form v-if="!quoteReceived" @submit.prevent="submitInsuranceForm">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <div>
                            <label class="text-sm font-medium text-gray-700">Size (Sqft) <span class="text-xs text-red-600">*</span></label>
                            <input type="number" v-model="insuranceForm.size_sqft"
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm" />
                            <span v-if="errors.size_sqft" class="text-red-500 text-sm">{{ errors.size_sqft[0] }}</span>
                        </div>
                        <div>
                            <label class="text-sm font-medium text-gray-700">Year Built <span class="text-xs text-red-600">*</span></label>
                            <select v-model="insuranceForm.year_built" class="mt-1 block w-full border-gray-300 rounded-md shadow-sm">
                                <option disabled value="">Select year built...</option>
                                <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
                            </select>
                            <span v-if="errors.year_built" class="text-red-500 text-sm">{{ errors.year_built[0] }}</span>
                        </div>

                        <div>
                            <label class="text-sm font-medium text-gray-700">Property Type <span class="text-xs text-red-600">*</span></label>
                            <select v-model="insuranceForm.property_type"
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm">
                                <option disabled="" value="">Select property type...</option>
                                <option value="SINGLE_FAMILY">Single Family</option>
                                <option value="DUPLEX">Duplex</option>
                                <option value="TRIPLEX">Triplex</option>
                                <option value="QUADPLEX">Quadplex</option>
                                <option value="APARTMENT_BUILDING">Apartment Building</option>
                                <option value="MOBILE_HOME">Mobile Home</option>
                                <option value="OTHER">Other</option>
                            </select>
                            <span v-if="errors.property_type" class="text-red-500 text-sm">{{ errors.property_type[0] }}</span>
                        </div>
                        <div>
                            <label class="text-sm font-medium text-gray-700">Number of Units</label>
                            <input v-model="insuranceForm.number_of_units" type="number"
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm" />
                            <span v-if="errors.number_of_units" class="text-red-500 text-sm">{{ errors.number_of_units[0] }}</span>
                        </div>
                        <div>
                            <label class="text-sm font-medium text-gray-700">Stories</label>
                            <input v-model="insuranceForm.stories" type="number"
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm" />
                            <span v-if="errors.stories" class="text-red-500 text-sm">{{ errors.stories[0] }}</span>
                        </div>
                    </div>

                    <p v-if="apiError" class="text-xs text-red-600 bg-red-100 p-2 mt-2">{{ apiError }}</p>

                    <div class="flex justify-end space-x-3 mt-6">
                        <button @click="closeInsuranceModal" type="button"
                            class="closeButton">
                            Close
                        </button>
                        <button type="submit" :disabled="isGetQuoteButtonDisabled || isSharedViewOnly"
                            class="primaryButton" :class="{ 'opacity-25': isGetQuoteButtonDisabled || isSharedViewOnly }">
                            Get Insurance Quote
                        </button>
                    </div>
                </form>

                <div v-else class="bg-white rounded-lg shadow-md p-6 max-w-lg mx-auto">
                    <div class="flex justify-between items-center border-b border-gray-400 pb-3">
                        <span class="text-purple-700 font-bold text-lg pr-3">STEADILY.</span>
                        <span class="text-purple-600 font-medium border-l pl-3 border-gray-400">
                            {{ property.full_address }}
                            <p class="text-sm">SIZE <strong class="text-black">
                                {{ quote.size_sqft }} SQFT
                            </strong></p>
                            <p class="text-sm">BUILT <strong class="text-black">
                                {{ quote.year_built }}
                            </strong></p>
                        </span>
                    </div>
                    <div class="font-medium border-t border-gray-400 pt-4 flex flex-col items-center">
                        <div class="text-center">
                            <p class="text-gray-500 uppercase text-sm">Annual Premium</p>
                            <h2 class="text-3xl font-bold text-purple-700">
                                ${{ quote.formatted_annual_premium }}
                            </h2>
                            <p
                                v-if="quote.annual_premium"
                                class="text-xs text-gray-500 mt-1"
                                title="Approximate. Actual monthly cost may vary depending on the payment plan you choose with Steadily."
                            >
                                ≈ ${{ monthlyEstimate(quote.annual_premium) }}/mo (estimated)
                            </p>
                        </div>
                    </div>
                </div>
                <div v-if="quoteReceived" class="flex justify-between items-center mt-6">
                    <button @click="closeInsuranceModal" type="button" class="closeButton mr-3">
                        Close
                    </button>
                    <div class="flex flex-wrap items-center justify-end gap-3">
                        <a
                            v-if="quote.start_url && !isSharedViewOnly"
                            target="_blank"
                            :href="quote.start_url"
                            class="primaryButton"
                        >
                            Get Insurance Quote
                        </a>
                        <span
                            v-else-if="quote.start_url && isSharedViewOnly"
                            class="primaryButton opacity-50 cursor-not-allowed pointer-events-none inline-block text-center"
                        >
                            Get Insurance Quote
                        </span>
                        <button
                            type="button"
                            :disabled="isSharedViewOnly"
                            @click="quoteReceived = false"
                            class="primaryButton disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                        >
                            Update Home Details
                        </button>
                    </div>
                </div>
            </div>
        </ModalJetstream>

        <!-- Upload Policy Modal -->
        <ModalJetstream :show="showUploadModal" :max-width="'lg'">
            <div class="bg-white rounded-lg p-6 m-2">
                <div class="mb-5 flex items-center justify-between border-b pb-3">
                    <h2 class="text-xl font-bold text-gray-800">Upload Insurance Policy</h2>
                    <button @click="closeUploadModal" class="text-gray-500 hover:text-gray-700 focus:outline-none">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div class="space-y-4">
                    <p class="text-sm text-gray-600">
                        Upload your landlord insurance policy document. Accepted formats: PDF, JPG, PNG, DOC, DOCX (max 10MB).
                    </p>

                    <input
                        ref="fileInput"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        :disabled="isSharedViewOnly"
                        @change="handleFileChange"
                        class="block w-full text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm p-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >

                    <p v-if="uploadError" class="text-xs text-red-600 bg-red-50 p-2 rounded">{{ uploadError }}</p>

                    <div class="flex justify-end space-x-3 mt-4">
                        <button @click="closeUploadModal" type="button" class="closeButton">
                            Cancel
                        </button>
                        <button
                            @click="uploadFile"
                            :disabled="!selectedFile || uploading || isSharedViewOnly"
                            class="primaryButton"
                            :class="{ 'opacity-25': !selectedFile || uploading || isSharedViewOnly }"
                        >
                            {{ uploading ? 'Uploading...' : 'Upload' }}
                        </button>
                    </div>
                </div>
            </div>
        </ModalJetstream>

        <!-- Delete Confirmation Modal -->
        <ModalJetstream :show="showDeleteModal" :max-width="'md'">
            <div class="bg-white rounded-lg p-6 m-2">
                <h2 class="text-lg font-bold text-gray-800 mb-3">Delete Insurance Policy</h2>
                <p class="text-sm text-gray-600 mb-5">Are you sure you want to delete this insurance policy? This action cannot be undone.</p>
                <div class="flex justify-end space-x-3">
                    <button @click="showDeleteModal = false" type="button" class="closeButton">
                        Cancel
                    </button>
                    <button
                        @click="deleteDocument"
                        :disabled="deleting || isSharedViewOnly"
                        class="inline-flex items-center px-4 py-2 bg-red-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-red-700 focus:outline-none transition"
                        :class="{ 'opacity-25': deleting || isSharedViewOnly }"
                    >
                        {{ deleting ? 'Deleting...' : 'Delete' }}
                    </button>
                </div>
            </div>
        </ModalJetstream>
    </div>
</template>

<script setup>
import { ref, computed, reactive } from 'vue'
import { router } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faXmark } from '@fortawesome/pro-light-svg-icons'
import ModalJetstream from '@/Jetstream/Modal.vue'
import ToolTip from '@/Shared/ToolTip.vue'
import axios from 'axios'

const route = (typeof window !== 'undefined' && window.route) || ((name, ...params) => {
    console.warn('Route function not available')
    return '#'
})

const props = defineProps({
    property: {
        type: Object,
        required: true
    },
    tab: {
        type: String,
        default: 'landlord-insurance'
    },
    tabData: {
        type: Object,
        default: () => ({})
    },
    propertyAccess: {
        type: String,
        default: 'owner',
    },
    viewOnlyTooltip: {
        type: Object,
        default: null,
    },
    isScreeningOnly: {
        type: Boolean,
        default: false,
    },
})

// RAC-10335: insurance is available on screening-only properties too (no upgrade gate).
const openUploadModal = () => {
    showUploadModal.value = true
}

const isSharedViewOnly = computed(() => props.propertyAccess === 'shared_view')

const insuranceData = computed(() => props.tabData || {})
const insuranceDocument = computed(() => props.tabData?.insurance_document || null)

const getInsuranceModal = ref(false)
const quoteReceived = ref(false)
const isGetQuoteButtonDisabled = ref(false)
const apiError = ref(null)
const errors = ref({})

const showUploadModal = ref(false)
const showDeleteModal = ref(false)
const selectedFile = ref(null)
const uploading = ref(false)
const deleting = ref(false)
const uploadError = ref(null)
const fileInput = ref(null)

const insuranceForm = reactive({
    property_id: null,
    state: null,
    zip_code: null,
    street_address: null,
    city: null,
    property_type: null,
    size_sqft: null,
    year_built: null,
    number_of_units: null,
    stories: null,
})

const quote = reactive({
    address: null,
    size_sqft: null,
    year_built: null,
    annual_premium: null,
    formatted_annual_premium: null,
    start_url: null,
})

const monthlyEstimate = (annualValue) => {
    if (annualValue === null || annualValue === undefined || annualValue === '') {
        return null
    }

    return (Number(annualValue) / 12).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })
}

const years = computed(() => {
    const startYear = 1800
    const currentYear = new Date().getFullYear()
    return Array.from({ length: currentYear - startYear + 1 }, (_, i) => currentYear - i)
})

const getInsurance = () => {
    if (isSharedViewOnly.value) {
        return
    }
    apiError.value = null
    errors.value = {}
    
    insuranceForm.property_id = props.property.id
    insuranceForm.state = props.property.state
    insuranceForm.zip_code = props.property.zip_code
    insuranceForm.street_address = props.property.address_1
    insuranceForm.city = props.property.city
    insuranceForm.property_type = insuranceData.value?.steadily_quote?.property_type ?? null
    insuranceForm.size_sqft = insuranceData.value?.steadily_quote?.size_sqft ?? null
    insuranceForm.year_built = insuranceData.value?.steadily_quote?.year_built ?? null
    insuranceForm.number_of_units = insuranceData.value?.steadily_quote?.number_of_units ?? null
    insuranceForm.stories = insuranceData.value?.steadily_quote?.stories ?? null

    if (insuranceData.value?.steadily_quote?.dp_3) {
        quote.address = props.property.full_address
        quote.size_sqft = insuranceForm.size_sqft
        quote.year_built = insuranceForm.year_built
        quote.annual_premium = insuranceData.value.steadily_quote.dp_3
        quote.formatted_annual_premium = insuranceData.value.steadily_quote.formatted_dp3
        quote.start_url = insuranceData.value.steadily_quote.start_url
        quoteReceived.value = true
    } else {
        quoteReceived.value = false
    }

    getInsuranceModal.value = true
}

const closeInsuranceModal = () => {
    getInsuranceModal.value = false
    quoteReceived.value = false
    apiError.value = null
    errors.value = {}
    Object.assign(quote, {
        address: null,
        size_sqft: null,
        year_built: null,
        annual_premium: null,
        formatted_annual_premium: null,
        start_url: null,
    })
}

const submitInsuranceForm = async () => {
    if (isSharedViewOnly.value) {
        return
    }
    apiError.value = null
    isGetQuoteButtonDisabled.value = true
    errors.value = {}
    
    try {
        const response = await axios.post(route('insurances.get-qoute'), insuranceForm)

        isGetQuoteButtonDisabled.value = false

        if (response.data.status === false) {
            apiError.value = response.data.message
            return
        }

        quote.address = props.property.full_address
        quote.size_sqft = insuranceForm.size_sqft
        quote.year_built = insuranceForm.year_built
        quote.annual_premium = response.data.quote.dp_3
        quote.formatted_annual_premium = response.data.quote.formatted_dp3
        quote.start_url = response.data.quote.start_url

        quoteReceived.value = true

        router.reload({
            only: ['tabData', 'tab'],
            preserveScroll: true,
            preserveState: true,
        })

    } catch (error) {
        isGetQuoteButtonDisabled.value = false
        if (error.response && error.response.data) {
            errors.value = error.response.data.errors || {}
        } else {
            console.error("An error occurred while fetching the quote:", error)
            apiError.value = "An error occurred while fetching the quote. Please try again."
        }
    }
}

const handleFileChange = (event) => {
    selectedFile.value = event.target.files[0] || null
    uploadError.value = null
}

const closeUploadModal = () => {
    showUploadModal.value = false
    selectedFile.value = null
    uploadError.value = null
    if (fileInput.value) {
        fileInput.value.value = ''
    }
}

const uploadFile = async () => {
    if (isSharedViewOnly.value) {
        return
    }
    if (!selectedFile.value) return

    uploading.value = true
    uploadError.value = null

    const formData = new FormData()
    formData.append('file', selectedFile.value)
    formData.append('type', 'insurance_policy')

    try {
        await axios.post(`/properties/${props.property.id}/documents/upload`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })

        closeUploadModal()
        router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
    } catch (error) {
        if (error.response?.data?.errors?.file) {
            uploadError.value = error.response.data.errors.file[0]
        } else {
            uploadError.value = error.response?.data?.message || 'An error occurred while uploading. Please try again.'
        }
    } finally {
        uploading.value = false
    }
}

const confirmDelete = () => {
    if (isSharedViewOnly.value) {
        return
    }
    showDeleteModal.value = true
}

const deleteDocument = async () => {
    if (isSharedViewOnly.value) {
        return
    }
    if (!insuranceDocument.value) return

    deleting.value = true

    try {
        await axios.delete(`/properties/${props.property.id}/documents/${insuranceDocument.value.id}`)
        showDeleteModal.value = false
        router.reload({ only: ['tabData'], preserveScroll: true, preserveState: true })
    } catch (error) {
        console.error('Failed to delete document:', error)
    } finally {
        deleting.value = false
    }
}
</script>
